import { useState } from 'react'
import { nf } from '@/shared/util/numbers'
import { formatDate } from '@/shared/util/dates'
import './ResendNonOpeners.css'

/**
 * "Send again to those who did not open", once — 23 Oct 2026.
 *
 * One panel for the newsletter (`crmResendNonOpeners`) and for letters to the
 * register (`crmSendShareholderMail`, mode `resend`). The caller says how to
 * count and how to send; the rules — once, not before 48 hours after the send
 * finished, same words with the subject open to change — are the server's,
 * and these only say when the button is worth showing. A count comes first:
 * the server knows who did not open, and the browser never needs to.
 */
const AFTER_MS = 48 * 3600_000

export interface ResendCounts {
  /** Messages it can tell about; absent where every row is tracked. */
  tracked?: number
  notOpened: number
  willSend: number
}

export interface ResendState {
  status: string
  finishedAt: Date | null
  subject: string
  resendOf: string | null
  resentAs: string | null
  resentAt: Date | null
  /** Why it cannot be sent again at all, where that is known up front. */
  blocked?: string
}

export function ResendNonOpeners({
  state,
  count,
  send,
  note,
}: {
  state: ResendState
  count: () => Promise<ResendCounts>
  send: (subject: string) => Promise<number>
  note?: string
}) {
  const [counts, setCounts] = useState<ResendCounts | null>(null)
  const [subject, setSubject] = useState(state.subject)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<number | null>(null)

  if (state.resendOf) {
    return <p className="micro muted">This is the resend to those who did not open the first one.</p>
  }
  if (state.resentAs) {
    return (
      <p className="micro muted">
        Sent again to those who did not open it{state.resentAt ? ` on ${formatDate(state.resentAt.toISOString())}` : ''}.
        It is sent again only once.
      </p>
    )
  }
  if (state.status !== 'finished' && state.status !== 'finished-with-errors') return null
  if (state.blocked) return <p className="micro muted">{state.blocked}</p>
  const readyAt = (state.finishedAt?.getTime() ?? 0) + AFTER_MS
  if (Date.now() < readyAt) {
    return (
      <p className="micro muted">
        Can be sent again to those who did not open it from{' '}
        {new Date(readyAt).toLocaleString('nb-NO', { dateStyle: 'medium', timeStyle: 'short' })}.
      </p>
    )
  }

  const run = <T,>(job: () => Promise<T>, then: (r: T) => void) => {
    setBusy(true)
    setError(null)
    job()
      .then(then)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false))
  }

  if (done !== null) {
    return <p className="micro">Sent again to {nf(done)} who did not open it.</p>
  }

  return (
    <div className="nresend">
      <div className="card-label">Send again to those who did not open</div>
      {!counts ? (
        <button className="btn btn--outline btn--tiny" disabled={busy} onClick={() => run(count, setCounts)}>
          {busy ? 'Counting…' : 'Count who did not open'}
        </button>
      ) : (
        <>
          <p className="micro">
            {counts.tracked !== undefined
              ? `${nf(counts.notOpened)} of ${nf(counts.tracked)} have not opened it`
              : `${nf(counts.notOpened)} have not opened it`}
            ; {nf(counts.willSend)} can still be sent to — the rest have unsubscribed, bounced or have no
            address now. {note}
          </p>
          <label className="nresend__field">
            <span className="micro">Subject</span>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
          <button
            className="btn btn--primary btn--tiny"
            disabled={busy || counts.willSend === 0 || !subject.trim()}
            onClick={() => run(() => send(subject.trim()), setDone)}
          >
            {busy ? 'Sending…' : `Send again to ${nf(counts.willSend)}`}
          </button>
          <p className="micro muted">Once only. The content is the same; only the subject can change.</p>
        </>
      )}
      {error && <p className="recform__error">{error}</p>}
    </div>
  )
}
